<template>
  <div class="tenant-color-dropdown">
    <div class="color-picker-content">
      <div class="color-picker-header">
        <h3 class="color-title">{{ Localizer.Localize('Select_a_color') }}</h3>
        <f-button 
          type="text" 
          @click="resetColor" 
          class="refresh-button"
          :title="Localizer.Localize('Header_Reset')"
          :outline="false"
          :focus="false"
          :bordered="false"
        >
          <f-icon icon="refresh" :size="16"/>
        </f-button>
      </div>
      <div class="color-grid">
        <div
          v-for="color in colorPalette"
          :key="color.name"
          class="color-swatch"
          :class="{ 'selected': isSelectedColor(color) }"
          :title="color.name"
          @click="selectColor(color)"
        >
        <div class="color-swatch-inner-box" :style="{ backgroundColor: color.value }">
          <svg
            v-if="isSelectedColor(color)"
            class="check-icon"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"
              fill="white"
            />
          </svg>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Emit, Watch } from 'vue-facing-decorator';
import { Localizer } from '@/api/Localizer';
import { COLOR_PALETTE, DEFAULT_TENANT_COLOR, ColorProp } from '../../constants/TenantSwitcherColors';

@Component
export default class TenantColorPickerContextMenu extends Vue {
  private Localizer = new Localizer(this.$store);
  private selectedColor: string = DEFAULT_TENANT_COLOR;

  @Prop({ default: DEFAULT_TENANT_COLOR })
  currentColor!: string;

  mounted() {
    this.selectedColor = this.currentColor;
  }

  @Watch('currentColor', { immediate: true })
  onCurrentColorChange(newColor: string) {
    this.selectedColor = newColor;
  }

  get colorPalette(): ColorProp[] {
    return COLOR_PALETTE;
  }

  isSelectedColor(color: ColorProp): boolean {
    return this.selectedColor === color.value;
  }

  selectColor(color: ColorProp) {
    this.selectedColor = color.value;
    this.applySelectedColor();
  }

  @Emit('colorSelected')
  applySelectedColor() {
    const selectedColorObj = this.colorPalette.find(c => c.value === this.selectedColor);
    this.$emit('close');
    return selectedColorObj || { name: 'Default', value: this.selectedColor };
  }

  @Emit('resetColor')
  resetColor() {
    this.selectedColor = DEFAULT_TENANT_COLOR;
    return { name: 'Default', value: DEFAULT_TENANT_COLOR };
  }
}
</script>

<style scoped>
.tenant-color-dropdown {
  min-width: 244px;
  background: var(--background-primary);
  border-radius: 8px;
  box-shadow: 0px 4px 8px 0px var(--anaplan-box-shadow);
  padding: 0;
}
.color-picker-content {
  padding: 16px;
}
.color-picker-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}
.color-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--anaplan-background);
  margin: 0;
}
.refresh-button {
  padding: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 4px;
  box-shadow: none !important;
}
.color-grid {
  display: flex;
  column-gap: 4px;
  justify-items: center;
}
.color-swatch {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  cursor: pointer;
  background: var(--background-primary);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.color-swatch-inner-box {
  width: 15px;
  height: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--accent-color-gray-25);
  border-radius: 2px;
}
.color-swatch-inner-box:hover {
  transform: scale(1.1);
}
.color-swatch.selected {
  border: 1px solid var(--accent-color-gray);
}
.check-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
</style>
